import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { LogIn } from "lucide-react";
import { ApiError } from "../../api/client";
import { BrandMark } from "../../components/BrandMark";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { useAuth } from "./useAuth";

// 登入畫面。刻意不套 AppShell:還沒登入的人不該看到側邊欄那一整排他點不進去的地方。
//
// 錯誤訊息一律照抄後端原句。後端刻意用同一句話回覆「查無此帳號」與「密碼錯誤」,
// 前端若自作聰明分開講,等於把後端擋掉的帳號列舉工具重新送回去。

export function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // 「記住我」預設不勾(業主 2026-10-10):公用電腦上什麼都不做的人,
  // 不該把一個 30 天的登入留給下一個坐下來的人。
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await login(email.trim(), password, remember);
      // 成功後由路由守衛導回原本要去的頁面,這裡不必自己 navigate。
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "無法連線到伺服器,請稍後再試。");
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg)] px-4 py-10">
      <div className="flex w-full max-w-sm flex-col gap-4 rounded-[var(--radius)] border border-[var(--border)] border-t-2 border-t-brand-gold bg-[var(--surface)] p-6">
        {/* LOGO 置中。業主 2026-09-13:「登入頁 不要出現金色那一圈」——
            那個金色筆刷圈拿掉了,**LOGO 留著**:登入頁是整個系統唯一一個
            還沒登入就看得到的畫面,沒有 LOGO 就沒有任何東西說得出
            「這是誰的系統」。
            圈沒了之後字標不必再避開圈的內徑,寬度從 w-20 放到 w-28,
            在同一塊留白裡讀起來還是同樣大小。 */}
        <div className="mx-auto flex items-center justify-center py-2">
          <BrandMark className="w-28" />
        </div>

        <h1 className="m-0 text-center text-xl font-semibold text-[var(--text)]">登入</h1>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <Input
            label="Email"
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Input
            label="密碼"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          <div className="flex flex-col gap-0.5">
            <label className="flex items-center gap-2 text-sm text-[var(--text)]">
              <input
                type="checkbox"
                name="remember"
                checked={remember}
                onChange={(event) => setRemember(event.target.checked)}
              />
              記住我(30 天內自動登入)
            </label>
          </div>

          {error ? (
            <p
              role="alert"
              className="m-0 rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--surface-2)] px-2.5 py-2 text-sm text-[var(--danger)]"
            >
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            icon={<LogIn aria-hidden="true" size={16} />}
            className="mt-1"
          >
            登入
          </Button>
        </form>

        {/* 忘記密碼的入口。沒有它,忘記密碼的人在這一頁上完全無路可走 ——
            這個系統沒有主機端的救援指令。 */}
        <Link to="/forgot" className="text-sm text-[var(--accent)] underline">
          忘記密碼?
        </Link>
      </div>
    </div>
  );
}
